import { FC, ReactNode, useCallback, MouseEventHandler } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import dibsCss from 'dibs-css';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link';
import getBuyerSupportLink from 'dibs-support-link/exports/getBuyerSupportLink';
import { trackEvent } from 'dibs-tracking';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';

import { PdpSharedBuyerProtection } from '../PdpSharedBuyerProtection/PdpSharedBuyerProtection';

import styles from './PdpSharedBuyerIncentives.scss';

import { PdpSharedBuyerIncentives_user$key } from './__generated__/PdpSharedBuyerIncentives_user.graphql';

const Incentive: FC<{ title: ReactNode; description: ReactNode }> = ({ title, description }) => {
    return (
        <div className={dibsCss.sassyFontBodyMedium}>
            <div className={classnames(dibsCss.sassyFontBodyMediumHeavy, dibsCss.mbXxsmall)}>
                {title}
            </div>
            {description}
        </div>
    );
};

const userFragment = graphql`
    fragment PdpSharedBuyerIncentives_user on User {
        ...PdpSharedBuyerProtection_user
    }
`;

export const PdpSharedBuyerIncentives: FC<{ user: PdpSharedBuyerIncentives_user$key | null }> = ({
    user: userRef,
}) => {
    const user = useFragment(userFragment, userRef);
    const intl = useIntl();

    const moreInfoClick: MouseEventHandler = useCallback(event => {
        trackEvent(
            {
                category: 'product',
                action: 'More Information click',
                label: 'pdp description | Authenticity Guarantee',
            },
            event
        );
    }, []);

    const incentivesLinkClick: MouseEventHandler = useCallback(event => {
        trackEvent(
            {
                category: 'product',
                action: 'Learn More Click',
                label: 'pdp description | 1stDibs Promise',
            },
            event
        );
    }, []);
    return (
        <div
            className={classnames(
                styles.wrapper,
                dibsCss.grid,
                dibsCss.gapXLarge,
                dibsCss.gapYSmall
            )}
        >
            <Incentive
                title={
                    <FormattedMessage
                        id="PdpSharedBuyerIncentives.authenticityGuarantee"
                        defaultMessage="Authenticity Guarantee"
                    />
                }
                description={
                    <FormattedMessage
                        id="PdpSharedBuyerIncentives.authenticityGuaranteeDescription"
                        defaultMessage="In the unlikely event there’s an issue with an item’s authenticity, you have up to one year to contact us for a refund. <link>More Information</link>"
                        values={{
                            link: text => (
                                <Link
                                    href={getBuyerSupportLink(
                                        '/en-us/articles/20416652048027-Authenticity-Guarantee-Policy'
                                    )}
                                    onClick={moreInfoClick}
                                    target="_blank"
                                >
                                    {text}
                                </Link>
                            ),
                        }}
                    />
                }
            />

            <PdpSharedBuyerProtection user={user} />

            <Incentive
                title={
                    <FormattedMessage
                        id="PdpSharedBuyerIncentives.freeCancellation"
                        defaultMessage="24-Hour Cancellation"
                    />
                }
                description={
                    <FormattedMessage
                        id="PdpSharedBuyerIncentives.freeCancellationDescription"
                        defaultMessage="You have a 24-hour grace period in which to reconsider your purchase, with no questions asked."
                    />
                }
            />

            <Incentive
                title={
                    <FormattedMessage
                        id="PdpSharedBuyerIncentives.vettedSellers"
                        defaultMessage="Vetted Professional Sellers"
                    />
                }
                description={
                    <FormattedMessage
                        id="PdpSharedBuyerIncentives.vettedSellersDescription"
                        defaultMessage="Our world-class sellers must adhere to strict standards for service and quality, maintaining the integrity of our listings."
                    />
                }
            />

            <Incentive
                title={
                    <FormattedMessage
                        id="PdpSharedBuyerIncentives.priceMatch"
                        defaultMessage="Price-Match Guarantee"
                    />
                }
                description={
                    <FormattedMessage
                        id="PdpSharedBuyerIncentives.priceMatchDescription"
                        defaultMessage="If you find that a seller listed the same item for a lower price elsewhere, we’ll match it."
                    />
                }
            />

            <Incentive
                title={
                    <FormattedMessage
                        id="PdpSharedBuyerIncentives.globalDelivery"
                        defaultMessage="Trusted Global Delivery"
                    />
                }
                description={
                    <FormattedMessage
                        id="PdpSharedBuyerIncentives.globalDeliveryDescription"
                        defaultMessage="Our best-in-class carrier network provides specialized shipping options worldwide, including custom delivery."
                    />
                }
            />

            <Link
                href={handleLocaleUrl('/about/ourpromise/', intl.locale)}
                target="_blank"
                onClick={incentivesLinkClick}
                className={dibsCss.sassyFontBodyMedium}
            >
                <FormattedMessage
                    id="PdpSharedBuyerIncentivesLink.learnMore"
                    defaultMessage="Learn More"
                />
            </Link>
        </div>
    );
};
